import { useQuery } from "@tanstack/react-query";

import type { HomeMetricName } from "@/lib/admin-api";
import { getHomeDeltas } from "@/lib/admin-api";

const HOME_DELTAS_QUERY_KEY = ["home", "deltas"];

/**
 * One metric's movement over the last seven days.
 *
 * `change` is already the subtraction: current minus the baseline. `since` is
 * the day that baseline was captured for, which is roughly but not always
 * exactly seven days back, and is shown so the number is never asked to imply
 * a window it does not cover.
 */
export interface HomeDelta {
  change: number;
  /** `YYYY-MM-DD` as sent by admin-service. */
  since: string;
}

/**
 * Week-on-week movement for the Home page's metrics.
 *
 * Returns a map keyed by metric name, holding ONLY the metrics that have both
 * a live count and a baseline. A metric is absent when either is missing, and
 * an absent metric renders with no delta at all.
 *
 * That absence is the whole contract. admin-service sends null for anything it
 * has not recorded, and null must not become 0 on the way through here: a zero
 * reads on the card as "no change this week", which is a claim about the
 * platform derived from an empty table. The snapshot history starts the day the
 * job first ran and cannot be backfilled, so "no delta yet" is the normal state
 * for the first week of any fresh environment.
 *
 * A failed request degrades the same way, to an empty map. Home is a launcher;
 * missing deltas cost it its delta lines, not the page.
 */
export function useHomeDeltas(): Partial<Record<HomeMetricName, HomeDelta>> {
  const { data } = useQuery({
    queryKey: HOME_DELTAS_QUERY_KEY,
    queryFn: getHomeDeltas,
  });

  return toDeltaMap(data?.metrics);
}

/**
 * Derives the deltas. Exported for its own tests: this is the arithmetic that
 * could quietly produce a wrong number, and it is worth testing without a
 * query client around it.
 */
export function toDeltaMap(
  metrics:
    | readonly {
        metric: HomeMetricName;
        current: number | null;
        weekAgo: number | null;
        weekAgoDate?: string;
      }[]
    | undefined,
): Partial<Record<HomeMetricName, HomeDelta>> {
  const out: Partial<Record<HomeMetricName, HomeDelta>> = {};
  if (!metrics) return out;

  for (const metric of metrics) {
    // `== null` on purpose: it catches null and undefined and lets a real 0
    // through, and a real 0 is a real count.
    if (metric.current == null || metric.weekAgo == null) continue;
    if (!metric.weekAgoDate) continue;

    out[metric.metric] = {
      change: metric.current - metric.weekAgo,
      since: metric.weekAgoDate,
    };
  }

  return out;
}

const DELTA_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

/**
 * "since 28 Aug" for the card's hint.
 *
 * UTC, because the snapshot days are UTC. Rendering in the browser's zone would
 * put two operators in different offices on different baseline days.
 */
export function formatDeltaSince(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  return `since ${DELTA_DATE_FORMAT.format(date)}`;
}

/**
 * The signed movement, e.g. `+24`, `-9`, `0`.
 *
 * The sign is load-bearing beyond arithmetic: the design system reads the
 * direction off the first character of this string to colour the delta, and
 * pairs it with `higherIsBetter` to decide whether up is good news. A string
 * with no leading sign would render neutral whichever way the metric moved.
 */
export function formatDeltaChange(change: number): string {
  if (change > 0) return `+${change.toLocaleString()}`;
  return change.toLocaleString();
}
